import React, {useMemo} from "react";
import {
  Box,
  Button,
  IconButton,
  TextField,
  Theme,
  Typography,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import {makeStyles} from "@material-ui/styles";
import {useSnackbar} from "notistack";
import FfairModal from "view/components/modal/FfairModal";
import FfairPaper from "view/components/FfairPaper";
import FfairTooltip from "view/components/Layout/Tooltip";
import {useMyVerifiedDomains} from "hooks/verifiedDomain/useMyVerifiedDomains";
import {useDeleteVerifiedDomain} from "hooks/verifiedDomain/useVerifiedDomainMutations";
import formatDate from "tools/formats/formatDate";

type Props = {
  open: boolean;
  onClose: () => void;
};

const useStyles = makeStyles((theme: Theme) => ({
  modal: {
    "&&": {
      overflow: "auto",
    },
  },
  paper: {
    width: 430,
    padding: theme.spacing(2, 2, 3),
    borderRadius: theme.spacing(0.25),
  },
  eyebrow: {
    color: "#2979FF",
    fontSize: 12,
    fontWeight: 500,
    letterSpacing: 1.2,
    lineHeight: "16px",
    marginBottom: theme.spacing(2),
    textTransform: "uppercase" as const,
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  backButton: {
    width: 18,
    height: 18,
    padding: 0,
    color: "#9E9E9E",
  },
  title: {
    color: "#212121",
    fontSize: 18,
    fontWeight: 500,
    lineHeight: "24px",
  },
  list: {
    display: "flex",
    flexDirection: "column" as const,
    gap: theme.spacing(3),
    maxHeight: 268,
    overflowY: "auto" as const,
  },
  rowHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1.5),
  },
  date: {
    color: "#757575",
    fontSize: 14,
    lineHeight: "20px",
  },
  cancelButton: {
    color: "#F44336",
    fontSize: 13,
    fontWeight: 600,
    lineHeight: "20px",
    minWidth: "auto",
    padding: 0,
    textTransform: "uppercase" as const,
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  urlField: {
    width: "100%",
    "& .MuiInputBase-root": {
      height: 48,
    },
    "& .MuiOutlinedInput-input": {
      color: "#212121",
      fontSize: 16,
      lineHeight: "24px",
      overflow: "hidden",
      padding: theme.spacing(1.5, 1.75),
      textOverflow: "ellipsis",
      whiteSpace: "nowrap" as const,
    },
    "& .MuiInputLabel-outlined": {
      color: "#9E9E9E",
      fontSize: 12,
    },
  },
  emptyText: {
    color: "#757575",
    fontSize: 14,
    lineHeight: "20px",
  },
}));

const MyPendingDomainsModal = ({open, onClose}: Props) => {
  const classes = useStyles();
  const [domains] = useMyVerifiedDomains(open);
  const {enqueueSnackbar} = useSnackbar();
  const deleteDomain = useDeleteVerifiedDomain();

  const pending = useMemo(() => domains.filter((d) => d.isApproved === null), [
    domains,
  ]);

  const handleDelete = async (id: string) => {
    try {
      await deleteDomain.mutateAsync(id);
      enqueueSnackbar("Verification request removed", {variant: "success"});
    } catch (e) {
      enqueueSnackbar("Failed to remove the request", {variant: "error"});
    }
  };

  return (
    <FfairModal
      open={open}
      onClose={onClose}
      isCalledByAnotherModal
      disableScrollLock
      className={classes.modal}
    >
      <FfairPaper className={classes.paper}>
        <Typography className={classes.eyebrow}>IFRAME</Typography>
        <Box className={classes.titleRow}>
          <IconButton
            aria-label="Back to iframe modal"
            className={classes.backButton}
            onClick={onClose}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography className={classes.title}>
            Waiting for Approval ({pending.length})
          </Typography>
        </Box>

        {pending.length === 0 ? (
          <Typography className={classes.emptyText}>
            You have no pending verification requests.
          </Typography>
        ) : (
          <Box className={classes.list}>
            {pending.map((domain) => (
              <Box key={domain.id}>
                <Box className={classes.rowHeader}>
                  <Typography className={classes.date}>
                    {formatDate(new Date(domain.createdAt), "dd.MM.yy HH:mm")}
                  </Typography>
                  <Button
                    className={classes.cancelButton}
                    onClick={() => handleDelete(domain.id)}
                    disabled={deleteDomain.isLoading}
                  >
                    CANCEL VERIFICATION
                  </Button>
                </Box>
                <FfairTooltip title={domain.url} placement="top-start">
                  <TextField
                    className={classes.urlField}
                    fullWidth
                    label="Insert your URL *"
                    value={domain.url}
                    variant="outlined"
                    InputProps={{readOnly: true}}
                  />
                </FfairTooltip>
              </Box>
            ))}
          </Box>
        )}
      </FfairPaper>
    </FfairModal>
  );
};

export default MyPendingDomainsModal;
